import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Users, UserPlus, CheckCircle2, AlertTriangle, FileSpreadsheet, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultBlock = ({ result }) => {
  if (!result) return null;
  return (
    <div className="mt-4 rounded-lg border bg-muted/20 p-4 text-sm space-y-1" data-testid="import-result">
      <div className="flex items-center gap-2 font-medium text-green-700">
        <CheckCircle2 size={16} /> Import complete
      </div>
      <div>Total rows: <strong>{result.total_rows}</strong></div>
      <div>Created: <strong>{result.created}</strong> · Updated: <strong>{result.updated}</strong> · Skipped: <strong>{result.skipped}</strong></div>
      {result.postings_created != null && <div>Job postings created: <strong>{result.postings_created}</strong></div>}
      {result.error_count > 0 && (
        <div className="text-amber-700">
          <div className="flex items-center gap-1 mt-1"><AlertTriangle size={14} /> {result.error_count} row error(s):</div>
          <ul className="list-disc ml-5 max-h-32 overflow-auto">
            {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

const ImportCard = ({ title, icon, description, endpoint, showTypeSelect, testid }) => {
  const [file, setFile] = useState(null);
  const [personnelType, setPersonnelType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!file) { toast.error('Choose a CSV file first'); return; }
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      if (showTypeSelect) fd.append('default_personnel_type', personnelType);
      const res = await axios.post(`${API}${endpoint}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(res.data);
      toast.success(`Imported: ${res.data.created} created, ${res.data.updated} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="p-2.5 bg-primary/10 rounded-lg text-primary">{icon}</div>
        <div>
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {showTypeSelect && (
        <div>
          <Label>Assign type to imported staff</Label>
          <Select value={personnelType} onValueChange={setPersonnelType}>
            <SelectTrigger data-testid="import-personnel-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-detect (clinicians paid hourly → Contractor, salaried/office → Employee)</SelectItem>
              <SelectItem value="Employee">All as Employee</SelectItem>
              <SelectItem value="Contractor">All as Contractor</SelectItem>
              <SelectItem value="Admin">All as Admin</SelectItem>
              <SelectItem value="Staff">All as Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>CSV file</Label>
        <input
          type="file"
          accept=".csv"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
          className="block w-full text-sm mt-1 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
          data-testid={`${testid}-file`}
        />
        {file && <p className="text-xs text-muted-foreground mt-1">{file.name}</p>}
      </div>

      <Button onClick={doImport} disabled={loading || !file} className="w-full" data-testid={`${testid}-submit`}>
        {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing… (may take a minute)</> : <><Upload size={16} className="mr-2" /> Import</>}
      </Button>

      <ResultBlock result={result} />
    </Card>
  );
};

export default function DataImport() {
  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl">
      <div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight flex items-center gap-3">
          <FileSpreadsheet className="text-blue-600" size={32} /> Import Data
        </h1>
        <p className="text-muted-foreground mt-2">
          Bulk-import from BambooHR CSV exports. Existing records (matched by email, then name) are updated; new ones are created. You can re-run anytime.
        </p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900" data-testid="import-tips">
        <strong>Tip:</strong> In BambooHR, export your employee report and your applicant/ATS report to CSV, then upload them below. Applicants automatically create any missing job postings.
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ImportCard
          title="Import Personnel"
          icon={<Users size={22} />}
          description="Employees & contractors (active or inactive). Upload each report; status comes from the file."
          endpoint="/import/personnel"
          showTypeSelect
          testid="import-personnel-card"
        />
        <ImportCard
          title="Import Applicants"
          icon={<UserPlus size={22} />}
          description="Candidates from your ATS. Missing job postings are created automatically."
          endpoint="/import/applicants"
          testid="import-applicants-card"
        />
      </div>
    </div>
  );
}
